import { useCallback, useEffect, useMemo, type ReactNode } from 'react'
import {
  useRecoilCallback,
  useRecoilTransaction_UNSTABLE,
  useRecoilValue,
} from 'recoil'
import { retargetCaseImageItems } from '@messenger/utils'
import {
  caseAtom,
  caseValuesAtom,
  itemAtomFamily,
  itemIdAtom,
  itemSelector,
  selectedPageAtom,
} from 'atoms'
import { canvasPageIdAtom, editorPagesAtom } from '@/atoms/_editorAtoms'
import { userAtom } from '@/atoms/_globalAtoms'
import {
  buildCaseImageTiles,
  caseImagesAtom,
  caseImageVariantsAtom,
  type CaseImageTile,
} from '@/atoms/caseImageAtoms'
import type { CaseImageRecord } from '@/api/caseImages'
import { EnhanceModal } from '@/components/dashboard/CaseDetails/CaseImages/EnhanceModal'
import {
  EnhanceContext,
  useAiImageEditsEnabled,
  useCaseImageVariants,
  useEnhanceImage,
  type EnhanceRequestOptions,
  type SharedEnhance,
} from '@/hooks/caseImages'
import { useFillCaseImages } from '@/hooks/editor/useFillCaseImages'
import { useUpdatePageItems } from '@/hooks/editor/useUpdatePageItems'
import {
  caseImageUrlAliases,
  itemsToSwapToEnhancedImage,
  type SwappableImageItem,
} from '@/utils/editor/swapItemsToEnhancedImage'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'

type EditorEnhanceProviderProps = {
  children: ReactNode
}

/**
 * Owns the editor's single Enhance (AI upscale) flow and its centred modal.
 * Every entry point inside the editor (the Case tab's photo grid, the
 * low-resolution warning on a canvas item, a canvas photo's "Revert changes"
 * and "Show enhancement") drives this one controller through
 * `EnhanceContext`, so there is one modal instance and one in-flight guard
 * no matter where the user started.
 *
 * After an accept, a revert or undoing one it refetches the case's image
 * list, whose `displayUrl` is the file the server now prefers (the accepted
 * upscale, or the original after a revert), refills the template slots, and
 * points the canvas items and the items of every other page the editor holds
 * that are already showing that photo at that file, so the change appears
 * where the person was working instead of waiting for them to reselect the
 * photo. The server has already moved every saved page of the case in the
 * transaction that accepted or reverted (`retargetCaseImagePlacements` in
 * `@messenger/utils`); moving the pages the editor holds keeps its next save
 * from writing the old file back.
 */
export const EditorEnhanceProvider = ({
  children,
}: EditorEnhanceProviderProps) => {
  const handleError = useErrorHandler('EditorEnhanceProvider')
  const user = useRecoilValue(userAtom)
  const caseData = useRecoilValue(caseAtom) as { id?: string } | null
  const caseIdFromValues = useRecoilValue(caseValuesAtom('id')) as {
    value?: string
  } | null
  const caseId = caseData?.id || caseIdFromValues?.value

  const aiImageEditsEnabled = useAiImageEditsEnabled()
  const { loadCaseImagesWithVariants, loadCaseImageVariants, loadPricing } =
    useCaseImageVariants()
  const fillCaseImages = useFillCaseImages()
  const { setEditorPagesItems, syncItemsToEditorPages } = useUpdatePageItems()

  // Point every canvas item that is showing this photo at the file the
  // server now prefers: forward to an enhancement after an accept or an
  // undone revert, back to the original after a revert. Items still holding
  // the template placeholder follow
  // the filled-images list instead and are left alone; items carrying a
  // Pintura render keep it.
  //
  // `freshImages` is the list the reload just returned, and the new file is
  // read from it rather than from the snapshot: a Recoil callback's snapshot
  // is the last committed state, so the list set by the reload is not in it
  // until React re-renders, and reading it there sees the old display URL and
  // concludes the item is already up to date. The canvas items and the
  // variant history are read from the snapshot, because what they need is the
  // state before the reload (the URLs an item could still be pointing at).
  //
  // Returns whether any item moved. It does not write the saved page itself:
  // sets made inside a Recoil callback are queued until the callback returns,
  // so a page sync from in here would collect the items with their old URL,
  // and Page.js reloads the canvas from the saved page whenever it changes,
  // which would put the old photo straight back.
  const swapCanvasItemsToShownImage = useRecoilCallback(
    ({ snapshot, set }) =>
      (caseImageId: string, freshImages: CaseImageRecord[]): boolean => {
        const image = freshImages.find((record) => record.id === caseImageId)
        if (!image) return false

        const variantsById =
          snapshot.getLoadable(caseImageVariantsAtom).valueMaybe() ?? {}
        const aliases = caseImageUrlAliases(image, variantsById[caseImageId])

        const ids = snapshot.getLoadable(itemIdAtom).valueMaybe() ?? []
        const items = ids
          .map((id: string) => {
            const item = snapshot
              .getLoadable(itemAtomFamily(id))
              .valueMaybe() as SwappableImageItem | undefined
            return item ? { ...item, id } : null
          })
          .filter(Boolean) as (SwappableImageItem & { id: string })[]

        const toSwap = itemsToSwapToEnhancedImage({
          items,
          aliases,
          nextUrl: image.displayUrl,
        })
        if (toSwap.length === 0) return false

        for (const id of toSwap) {
          set(itemSelector({ id, path: 'image.url' }), image.displayUrl)
        }
        return true
      },
    [],
  )

  // Point the items of every page the editor holds, except the one on the
  // canvas, at the photo's display file, by the rule the server used on the
  // saved pages. The page on the canvas is moved through the canvas instead
  // (`swapCanvasItemsToShownImage`): its saved items can be behind the
  // canvas, and writing them would make Page.js reload the canvas from them.
  // The page on the canvas is the one the canvas was hydrated from
  // (`canvasPageIdAtom`); the selected page stands in only before a page has
  // been hydrated, since a page navigation selects the next page before the
  // canvas is loaded from it. A page in which nothing moves keeps its
  // object, so nothing re-renders.
  const retargetOtherEditorPages = useRecoilCallback(
    ({ snapshot, set }) =>
      (caseImageId: string, freshImages: CaseImageRecord[]) => {
        const image = freshImages.find((record) => record.id === caseImageId)
        if (!image) return
        const variantsById =
          snapshot.getLoadable(caseImageVariantsAtom).valueMaybe() ?? {}
        const aliases = caseImageUrlAliases(image, variantsById[caseImageId])
        const canvasPageId = snapshot.getLoadable(canvasPageIdAtom).valueMaybe()
        const canvasIndex =
          (snapshot.getLoadable(selectedPageAtom).valueMaybe() as number) - 1
        set(editorPagesAtom, (pages) => {
          let moved = false
          const next = pages.map((page, index) => {
            const isCanvasPage = canvasPageId
              ? page.product?.orderPageId === canvasPageId
              : index === canvasIndex
            if (isCanvasPage) return page
            const result = retargetCaseImageItems(
              page,
              aliases,
              image.displayUrl,
            )
            if (result.moved > 0) moved = true
            return result.content
          })
          return moved ? next : pages
        })
      },
    [],
  )

  // Show what the server prefers for a photo, from the image list a load
  // just returned: refill the template slots, move the other pages the
  // editor holds, then point the canvas items showing that photo at its
  // display file and save them to the editor pages.
  const showServerImage = useCallback(
    (caseImageId: string, images: CaseImageRecord[]) => {
      fillCaseImages(images)
      retargetOtherEditorPages(caseImageId, images)
      if (!swapCanvasItemsToShownImage(caseImageId, images)) return
      // The item atoms are the canvas; editorPagesAtom is what gets saved as
      // the page template. Both have to move together or the enhanced photo
      // is lost on the next load. This runs after the swap callback has
      // returned, so its item changes have been applied and the sync reads
      // them, the same order every other single-image edit uses (see
      // syncImageEditToEditorPages).
      setEditorPagesItems(syncItemsToEditorPages())
    },
    [
      fillCaseImages,
      retargetOtherEditorPages,
      setEditorPagesItems,
      swapCanvasItemsToShownImage,
      syncItemsToEditorPages,
    ],
  )

  const refreshCaseImages = useCallback(
    async (caseImageId: string) => {
      if (!caseId) return
      // Waits for the variants too: the grid tile's file and its Revert or
      // Show enhancement item are read off them, so the flow that called
      // this must not close before they answer. The list is the newest load's
      // (`loadCaseImagesWithVariants`), so a reload a newer one overtook
      // never moves the slots or the canvas back to an older file.
      const { images } = await loadCaseImagesWithVariants(caseId)
      showServerImage(caseImageId, images)
    },
    [caseId, loadCaseImagesWithVariants, showServerImage],
  )

  // A canvas photo offers "Show enhancement" while its photo's enhancement is
  // reverted, which only the variants say (the image list's display file is
  // then the photo itself), and the editor's own load fetches the image list
  // alone. So the variants are fetched whenever the editor's case changes
  // while AI image edits are on, the switch's answer arriving included; a
  // photo reverted before the editor opened then offers its way back without
  // the Case tab ever being opened. This is a fetch on a change of case,
  // which no event handler sees, hence the effect. A failure is logged and
  // leaves the canvas offering nothing for a reverted photo.
  useEffect(() => {
    if (caseId && aiImageEditsEnabled) void loadCaseImageVariants(caseId)
  }, [aiImageEditsEnabled, caseId, loadCaseImageVariants])

  // A finished generation: reload the variants so the Case tab's tiles hold
  // it, and the next Enhance from the grid reopens it for free.
  const reloadCaseImageVariants = useCallback(async () => {
    if (caseId) await loadCaseImageVariants(caseId)
  }, [caseId, loadCaseImageVariants])

  const enhance = useEnhanceImage({
    userId: user?.id,
    onShownImageChanged: refreshCaseImages,
    onVariantsChanged: reloadCaseImageVariants,
    onError: handleError,
  })

  // Read the grid tile for an image from what the loads just stored. A
  // callback's snapshot is the last committed state, and the loads' writes
  // are committed only when React next renders, so a snapshot read right
  // after them still holds the list and variants from before: a photo
  // deleted since the warning was drawn would still open, and a finished
  // upscale the reload just found would be missed and paid for again. A
  // transaction reads the queued state instead (as `useUpdatePageItems`
  // does). Selectors cannot be read in a transaction, so the tile is built
  // from the atoms the grid's selector reads, with the same builder.
  // Transactions discard their return value, so the tile comes back
  // through `found`.
  const findTileTransaction = useRecoilTransaction_UNSTABLE(
    ({ get }) =>
      (caseImageId: string, found: CaseImageTile[]) => {
        const tile = buildCaseImageTiles(
          get(caseImagesAtom),
          get(caseImageVariantsAtom),
        ).find((candidate) => candidate.image.id === caseImageId)
        if (tile) found.push(tile)
      },
    [],
  )
  const readTile = useCallback(
    (caseImageId: string): CaseImageTile | null => {
      const found: CaseImageTile[] = []
      findTileTransaction(caseImageId, found)
      return found[0] ?? null
    },
    [findTileTransaction],
  )

  const { requestEnhance, enhanceLocked, openRevert, showEnhancement } = enhance

  /**
   * Reload the case's images, variants and pricing, then hand the photo's
   * fresh grid tile and the reloaded image list to `act`. Nothing is handed
   * on when the photo is gone, or when the image list or the variants did
   * not load (the failure is reported): Enhance, Revert and Show enhancement
   * all decide from the variants, and with none they would quietly do
   * nothing or offer a paid generation the photo already has. Refused while
   * a generation runs, so nothing reloads the grid for a request the hook
   * would refuse anyway.
   */
  const withFreshTile = useCallback(
    async (
      caseImageId: string,
      act: (tile: CaseImageTile, images: CaseImageRecord[]) => void,
    ) => {
      if (!caseId || enhanceLocked) return
      let images: CaseImageRecord[]
      try {
        const loaded = await loadCaseImagesWithVariants(caseId)
        if (loaded.variantsError) throw loaded.variantsError
        images = loaded.images
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err))
        handleError(error.message, error)
        return
      }
      // Null when the image was deleted since the canvas was drawn.
      const tile = readTile(caseImageId)
      if (tile) act(tile, images)
    },
    [caseId, enhanceLocked, handleError, loadCaseImagesWithVariants, readTile],
  )

  const enhanceCaseImage = useCallback(
    // The disclosure reads the tile's variants (a finished upscale is
    // reused) and the price next, so this waits for them too.
    (caseImageId: string, options?: EnhanceRequestOptions) =>
      withFreshTile(caseImageId, (tile) => requestEnhance(tile, options)),
    [requestEnhance, withFreshTile],
  )

  // A canvas photo's revert and its undo act on the photo as the server now
  // holds it, so the confirmation saves the mark on the newest accepted
  // enhancement. When the reload shows the photo already moved (someone
  // reverted it, or undid that, since the canvas was drawn), nothing is
  // saved and the canvas is moved to what the server shows instead.
  const revertCaseImage = useCallback(
    (caseImageId: string) =>
      withFreshTile(caseImageId, (tile, images) => {
        if (tile.display.shownVariant) openRevert(tile)
        else showServerImage(caseImageId, images)
      }),
    [openRevert, showServerImage, withFreshTile],
  )

  const showCaseImageEnhancement = useCallback(
    (caseImageId: string) =>
      withFreshTile(caseImageId, (tile, images) => {
        if (tile.display.reverted) void showEnhancement(tile)
        else showServerImage(caseImageId, images)
      }),
    [showEnhancement, showServerImage, withFreshTile],
  )

  const value = useMemo<SharedEnhance>(
    () => ({
      enhance,
      enhanceCaseImage,
      revertCaseImage,
      showCaseImageEnhancement,
      ensurePricing: loadPricing,
      refreshShownImage: refreshCaseImages,
    }),
    [
      enhance,
      enhanceCaseImage,
      loadPricing,
      refreshCaseImages,
      revertCaseImage,
      showCaseImageEnhancement,
    ],
  )

  return (
    <EnhanceContext.Provider value={value}>
      {children}
      <EnhanceModal enhance={enhance} />
    </EnhanceContext.Provider>
  )
}
